import {bindActionCreators} from '@reduxjs/toolkit';
import {authenticateSession, authenticateUser, getSsoLoginUrl, registerSessionUser} from 'actions/session-actions';
import InputField from 'components/forms/components/input-field';
import formManager from 'components/forms/form-manager';
import ProgressPulsingCircles from 'components/progress/progress-pulsing-circles';
import withLDFlags from 'components/with-ld-flags';
import ErrorCodes from 'constants/error-codes';
import SSOErrorTypes from 'constants/sso-error-types';
import UiConfig from 'constants/ui-config';
import messages from 'intl/anonymous-messages';
import globalMessages from 'intl/global-messages';
import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';
import React from 'react';
import ReactDOM from 'react-dom';
import {FormattedMessage, injectIntl} from 'react-intl';
import {connect} from 'redux-store';
import FormUtils from 'utils/form-utils';
import UIErrorCodeUtils from 'utils/ui-error-code-utils';
import FeatureBranchLogin from './components/feature-branch-login';
import LocalEnvironmentLogin from './components/local-environment-login';
import S from './login.less';

import type {FormManagerProps} from 'components/forms/utils/form-manager-types';
import type {Location} from 'history';
import type {MessageDescriptor, WrappedComponentProps} from 'react-intl';
import type {ConnectedProps} from 'react-redux';
import type {RouteComponentProps, StaticContext} from 'react-router';
import type {LDProps} from 'types/launch-darkly-types';

const loginShowClientInput = __LOGIN_SHOW_CLIENT_INPUT__;
const allowFeatureBranchTestingOptions = UiConfig.ALLOW_FEATURE_BRANCH_TESTING_OPTIONS ?? false;
const allowLocalTestingOptions = UiConfig.ALLOW_LOCAL_TESTING_OPTIONS ?? false;

type PropsFromRedux = ConnectedProps<typeof connector>;

type LocationState = {
    nextLocation: Location;
};

type Props = LDProps &
    WrappedComponentProps &
    PropsFromRedux &
    Pick<RouteComponentProps<object, StaticContext, LocationState>, 'history' | 'location'> &
    FormManagerProps;

type State = {
    inProgress: boolean;
    focusedElement: unknown;
    loginMinimized: boolean;
    ssoErrorType?: string;
    ssoErrorTraceId?: string;
};

class Login extends React.Component<Props, State> {
    static defaultProps: Partial<Props> = {
        flags: {},
        getSsoLoginUrl: () => null,
        loginError: null,
        registerSessionUser: () => null,
    };

    private readonly nextLocation: unknown;

    constructor(props: Readonly<Props>) {
        super(props);

        const {flags, location, settings} = props;
        const searchParams = new URLSearchParams(location.search);
        const ssoErrorType = searchParams.get('error');
        const ssoErrorTraceId = searchParams.get('errorTraceId');

        // if both login options are available, start with the login form minimized
        const loginMinimized = flags?.loginOptions
            ? settings.get('ssoEnabled') && settings.get('allowSsoLogin') && settings.get('allowLocalLogin')
            : settings.get('ssoEnabled');

        this.state = {
            inProgress: false,
            focusedElement: null,
            loginMinimized,
        };

        // nextLocation is set by signed-in-routes.js when you try to view a signed-in route without being logged in.
        // We need to redirect you to that location after logging in
        this.nextLocation =
            isNil(location) ||
            isNil(location.state) ||
            isNil(location.state.nextLocation) ||
            location.state.nextLocation.pathname.includes('reset-password')
                ? null
                : location.state.nextLocation;

        if (!isNil(ssoErrorType)) {
            // @ts-expect-error TS2540: Cannot assign to ssoErrorType because it is a read-only property.
            this.state.ssoErrorType = ssoErrorType;
            // @ts-expect-error TS2540: Cannot assign to ssoErrorTraceId because it is a read-only property.
            this.state.ssoErrorTraceId = ssoErrorTraceId;
        }
    }

    async componentDidMount() {
        if (!isNil(this.state.ssoErrorType)) {
            // clear any errors from location once we've had a chance to deal with them
            const nextLocation = cloneDeep(this.props.location);
            const searchParams = new URLSearchParams(nextLocation.search);
            searchParams.delete('error');
            searchParams.delete('errorTraceId');
            nextLocation.search = searchParams.toString();
            this.props.history.replace(nextLocation);
        }

        const {location, flags, settings} = this.props;
        const searchParams = new URLSearchParams(location.search);
        const useSimCaptureLogin = searchParams.get('useSimCaptureLogin') === 'true';

        // Only try to do this when the flags load in after first render
        const isLaerdalConnectEnabled =
            flags.useLaerdalConnectAuthentication && !isNil(settings.get('laerdalOrganizationId'));
        if (isLaerdalConnectEnabled && !useSimCaptureLogin) {
            this.props.history.replace('/login/laerdal');
        }
    }

    focus(element) {
        // @ts-expect-error TS(2339) FIXME: Property 'focus' does not exist on type 'Element |... Remove this comment to see the full error message
        // eslint-disable-next-line react/no-find-dom-node -- SCLD-16839
        ReactDOM.findDOMNode(element).focus();
    }

    inputFocus(ref) {
        this.setState({
            focusedElement: ref,
        });
    }

    login(data) {
        const username = data.get('username');
        data = data.set('username', username.trim());
        if (isNil(data.get('clientSubdomain'))) {
            const urlParts = window.location.hostname.split('.');
            if (urlParts.length !== 3) {
                throw new Error(
                    'Invalid system configuration. client should be passed explicitly, or inferrable from URL',
                );
            }
            data = data.set('clientSubdomain', urlParts[0]);
        }

        this.setState({
            focusedElement: null,
            inProgress: true,
        });

        this.props.authenticateUser(data, this.onLoginSuccess, this.onLoginFailure);
    }

    onLoginSuccess = async () => {
        const {history, location} = this.props;
        const urlParams = new URLSearchParams(location.search);

        if (!isNil(this.nextLocation)) {
            // If nextLocation is set, this is redirecting to another route within SCLD.
            history.replace(this.nextLocation);
        } else if (urlParams.has('redirect')) {
            // There is no redirect from a SCLD route, but a query param for a redirect is being used.
            const redirect = urlParams.get('redirect');

            // Locally, a valid redirect will only be the full URL with the scheme and origin for http://localhost.
            // In all other environments, only path parameters will be considered valid redirects.
            // This will be appended to the current simcapture host origin.
            if (__UI_SERVICE__ === 'local') {
                const redirectIsValid = redirect.startsWith('http://localhost:');
                if (redirectIsValid) {
                    window.location.assign(redirect);
                } else {
                    history.replace('/');
                }
            } else {
                const redirectIsValid = redirect.startsWith('/');
                if (redirectIsValid) {
                    window.location.assign(`${window.location.origin}${redirect}`);
                } else {
                    history.replace('/');
                }
            }
        } else {
            // Go to the dashboard if no redirect is specified.
            history.replace('/');
        }
    };

    onLoginFailure = (meteorError) => {
        this.setState({
            inProgress: false,
        });

        if (meteorError) {
            const errorCode = meteorError.get('errorCode');

            if (UIErrorCodeUtils.errorCodesAreEqual(errorCode, ErrorCodes.LOGIN_ERROR)) {
                // select password field
                // @ts-expect-error TS(2339) FIXME: Property 'focus' does not exist on type 'ReactInst... Remove this comment to see the full error message
                // eslint-disable-next-line react/no-string-refs -- SCLD-18005
                this.refs.password.focus();
                // clear password field
                // @ts-expect-error TS(2339) FIXME: Property 'reset' does not exist on type 'ReactInst... Remove this comment to see the full error message
                // eslint-disable-next-line react/no-string-refs -- SCLD-18005
                this.refs.password.reset();
            } else if (UIErrorCodeUtils.errorCodesAreEqual(errorCode, ErrorCodes.LOGIN_CLIENT_NOT_FOUND)) {
                // client field is sometimes hidden so check for it
                // eslint-disable-next-line react/no-string-refs -- SCLD-18005
                if (this.refs.client) {
                    // @ts-expect-error TS(2339) FIXME: Property 'focus' does not exist on type 'ReactInst... Remove this comment to see the full error message
                    // eslint-disable-next-line react/no-string-refs -- SCLD-18005
                    this.refs.client.focus();
                    // @ts-expect-error TS(2339) FIXME: Property 'reset' does not exist on type 'ReactInst... Remove this comment to see the full error message
                    // eslint-disable-next-line react/no-string-refs -- SCLD-18005
                    this.refs.client.reset();
                }
            }
        }
    };

    requestResetPassword = () => {
        this.props.history.push('/request-reset-password');
    };

    // Redirects the user to the appropriate SSO IDP login url for their client.
    onSsoLogin = () => {
        this.props.getSsoLoginUrl((ssoLoginUrl) => {
            window.location.href = ssoLoginUrl;
        });
    };

    getLoginErrorText(loginErrorCode: Props['loginError']) {
        let message: MessageDescriptor;
        switch (loginErrorCode.get('errorCode')) {
            case ErrorCodes.TOKEN_INVALID.errorCode:
                message = messages.tokenInvalid;
                break;
            case ErrorCodes.LOGIN_CLIENT_NOT_FOUND.errorCode:
                message = messages.clientNotFound;
                break;
            case ErrorCodes.LOGIN_LOCKOUT.errorCode:
                message = messages.loginLockout;
                break;
            case ErrorCodes.LOGIN_ERROR.errorCode:
                message = messages.loginError;
                break;
            case ErrorCodes.LOGIN_INACTIVE.errorCode:
                message = messages.loginInactive;
                break;
            default:
                message = messages.unknownError;
        }
        return (
            <p className={S.errorMessage}>
                <FormattedMessage {...message} />
            </p>
        );
    }

    getSsoErrorText(ssoErrorType, ssoErrorTraceId) {
        let message;

        switch (ssoErrorType) {
            case SSOErrorTypes.MISSING_RELAY_STATE:
                // we should use this message if the user can't feasibly recover from the error
                message = messages.loginErrorSsoErrorOccurred;
                break;
            case SSOErrorTypes.INVALID_RELAY_STATE:
                message = messages.loginErrorSsoInvalidRelayState;
                break;
            case SSOErrorTypes.INVALID_SSO_EXTERNAL_ID:
                message = messages.loginErrorSsoInvalidSsoExternalId;
                break;
            case SSOErrorTypes.SSO_NOT_ENABLED:
                message = messages.loginErrorSsoNotEnabled;
                break;
            case SSOErrorTypes.USER_INACTIVE:
                message = messages.loginErrorSsoUserInactive;
                break;
            case SSOErrorTypes.USER_NOT_FOUND:
                message = messages.loginErrorSsoUserNotFound;
                break;
            case SSOErrorTypes.UNKNOWN_ERROR:
                message = messages.loginErrorSsoUnknownError;
                break;
            default:
                message = messages.loginErrorSsoUnknownError;
        }

        return (
            <div>
                <p className={S.errorMessage}>
                    <FormattedMessage {...message} />
                </p>
                <p className={S.errorMessage}>
                    <FormattedMessage {...messages.loginErrorTraceId} values={{traceId: ssoErrorTraceId}} />
                </p>
            </div>
        );
    }

    onLoginMinimizeClick = () => {
        this.setState(
            {
                loginMinimized: false,
            },
            () => {
                if (loginShowClientInput) {
                    // @ts-expect-error TS(2339) FIXME: Property 'focus' does not exist on type 'ReactInst... Remove this comment to see the full error message
                    // eslint-disable-next-line react/no-string-refs -- SCLD-18005
                    this.refs.client.focus();
                } else {
                    // @ts-expect-error TS(2339) FIXME: Property 'focus' does not exist on type 'ReactInst... Remove this comment to see the full error message
                    // eslint-disable-next-line react/no-string-refs -- SCLD-18005
                    this.refs.username.focus();
                }
            },
        );
    };

    render() {
        const {flags, intl, loginError: loginErrorCode, settings, valid} = this.props;
        const {inProgress, loginMinimized, ssoErrorTraceId, ssoErrorType} = this.state;
        const {formatMessage} = intl;

        const onSubmit = (...args) => {
            // Block submissions if this is minimized
            if (loginMinimized) {
                return;
            } else {
                this.props.handleSubmit(this.login.bind(this))(...args);
            }
        };

        let tabIndex = 0;

        let submitButton;
        if (loginMinimized) {
            // Make the button a pretty blue if the login prompt is minimized
            submitButton = S.submitButton;
        } else if (valid) {
            submitButton = S.submitButton;
        } else {
            submitButton = S.submitButtonDisabled;
        }

        const onKeyDown = (event) => {
            if (event.key === 'Enter') {
                onSubmit(event);
            }
        };

        let errorText = !isNil(loginErrorCode) ? this.getLoginErrorText(loginErrorCode) : null;
        if (isNil(errorText) && !isNil(ssoErrorType)) {
            errorText = this.getSsoErrorText(ssoErrorType, ssoErrorTraceId);
        }

        let usernameOrEmailLabel;
        let usernameOrEmailPlaceHolder;
        if (!settings.get('uniqueDomain', false)) {
            usernameOrEmailLabel = messages.email;
            usernameOrEmailPlaceHolder = messages.emailPlaceholder;
        } else {
            usernameOrEmailLabel = messages.username;
            usernameOrEmailPlaceHolder = messages.usernamePlaceholder;
        }

        let displaySsoLoginOption: boolean;
        let displayLocalLoginOption: boolean;

        if (flags?.loginOptions) {
            displaySsoLoginOption = settings.get('ssoEnabled') && settings.get('allowSsoLogin');
            // Cluster clients don't have authSettings available at this point, so default to true
            displayLocalLoginOption = settings.get('allowLocalLogin') ?? true;
        } else {
            displaySsoLoginOption = settings.get('ssoEnabled');
            displayLocalLoginOption = true;
        }

        // local and preview env overrides bc settings are not fetched without client subdomain
        if (allowLocalTestingOptions || allowFeatureBranchTestingOptions) {
            displayLocalLoginOption = true;
            displaySsoLoginOption = settings.get('ssoEnabled');
        }

        return (
            <div className={S.main}>
                <form className={S.form} noValidate onSubmit={FormUtils.preventSubmit}>
                    {displaySsoLoginOption && (
                        <div>
                            <div
                                className={inProgress ? S.ssoButtonDisabled : S.ssoButton}
                                tabIndex={valid ? ++tabIndex : -1}
                                onClick={this.onSsoLogin}>
                                <i className={S.ssoIcon}>vpn_key</i>
                                <FormattedMessage {...messages.ssoLogin} />
                            </div>
                        </div>
                    )}

                    {displaySsoLoginOption && displayLocalLoginOption && (
                        <div className={S.ssoDivider}>
                            <FormattedMessage {...globalMessages.or} />
                        </div>
                    )}

                    {displayLocalLoginOption && (
                        <React.Fragment>
                            <div
                                className={S.inputsContainer}
                                style={{
                                    maxHeight: loginMinimized ? '0' : '100vh',
                                }}>
                                {loginShowClientInput && (
                                    <div>
                                        <label htmlFor='client_name' className={S.labelFirst}>
                                            <FormattedMessage {...messages.client} />
                                        </label>
                                        <InputField
                                            autoFocus={true}
                                            // eslint-disable-next-line react/no-string-refs -- SCLD-18005
                                            ref='client'
                                            name='clientSubdomain'
                                            className={S.input}
                                            id='client_name'
                                            autoCapitalize='off'
                                            required={true}
                                            tabIndex={++tabIndex}
                                            onEnter={onSubmit}
                                            onFocus={this.inputFocus.bind(this, 'client')}
                                            disabled={loginMinimized}
                                        />
                                    </div>
                                )}
                                <label
                                    htmlFor='username_login'
                                    className={loginShowClientInput ? S.label : S.labelFirst}>
                                    <FormattedMessage {...usernameOrEmailLabel} />
                                </label>
                                <InputField
                                    // eslint-disable-next-line react/no-string-refs -- SCLD-18005
                                    ref='username'
                                    name='username'
                                    className={S.input}
                                    autoCapitalize='off'
                                    id='username_login'
                                    required={true}
                                    onFocus={this.inputFocus.bind(this, 'username')}
                                    placeholder={formatMessage(usernameOrEmailPlaceHolder)}
                                    tabIndex={++tabIndex}
                                    onEnter={onSubmit}
                                    disabled={loginMinimized}
                                />
                                <label htmlFor='password_login' className={S.label}>
                                    <FormattedMessage {...messages.password} />
                                </label>
                                <InputField
                                    // eslint-disable-next-line react/no-string-refs -- SCLD-18005
                                    ref='password'
                                    name='password'
                                    className={S.input}
                                    autoCapitalize='off'
                                    autoComplete='on'
                                    placeholder={formatMessage(messages.passwordPlaceholder)}
                                    tabIndex={++tabIndex}
                                    required={true}
                                    type='password'
                                    onFocus={this.inputFocus.bind(this, 'password')}
                                    onEnter={onSubmit}
                                    disabled={loginMinimized}
                                />
                                <div className={S.forgotPasswordText} onClick={this.requestResetPassword}>
                                    <FormattedMessage {...messages.forgotPassword} />
                                </div>
                            </div>

                            {inProgress ? (
                                <div className={S.loadingProgress}>
                                    <ProgressPulsingCircles />
                                </div>
                            ) : (
                                <div
                                    className={submitButton}
                                    onKeyDown={onKeyDown}
                                    // eslint-disable-next-line react/no-string-refs -- SCLD-18005
                                    ref='submitButton'
                                    id='submit_button'
                                    tabIndex={valid ? ++tabIndex : -1}
                                    onClick={loginMinimized ? this.onLoginMinimizeClick : onSubmit}>
                                    <FormattedMessage {...messages.login} />
                                </div>
                            )}
                        </React.Fragment>
                    )}

                    <div className={S.errorText}>{errorText}</div>
                </form>
                {allowLocalTestingOptions && <LocalEnvironmentLogin onLogin={this.login.bind(this)} />}
                {allowFeatureBranchTestingOptions && <FeatureBranchLogin onLogin={this.login.bind(this)} />}
            </div>
        );
    }
}

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators(
        {
            authenticateSession,
            authenticateUser,
            getSsoLoginUrl,
            registerSessionUser,
        },
        dispatch,
    );
};

const connector = connect(
    (state) => ({
        loginError: state.session.loginError,
        client: state.session.client,
        settings: state.session.settings,
    }),
    mapDispatchToProps,
    null,
    {forwardRef: true},
);

export default connector(withLDFlags(injectIntl(formManager()(Login))));
